import {connect} from 'react-redux';
import {FC, useState} from 'react';
import { RouteComponentProps } from 'react-router-dom';

import { ContentLocalesI } from '~src/interfaces/session.types';
import logger from '~src/services/logger/LlsLogger';
import { translateContent } from '~src/services/translationServices';
import { nameDescrFields } from '~src/shared/form/translatableSectionForm/defaultFields';
import { deleteScale, editScale, EditScaleFormData } from '~src/modules/scales/actions/scaleActions';
import ItemDetailsHeader from '~src/shared/headers/itemDetalsHeader/ItemDetailsHeader';
import TranslatableSectionForm, {
  FormValuesI,
} from '~src/shared/form/translatableSectionForm/TranslatableSectionForm';
import { Scale } from '~src/interfaces/scale.interface';
import { StoreI } from '~src/rootReducer';
import {
  ApiReduxResp,
  getLoggingContext,
  isError,
  isSuccess,
  ThunkMWDispatch
} from '~src/services/apiMiddleware';
import { SectionOpt } from '~src/shared/form/translatableSectionForm/interfaces';
import { routes } from '~src/routingConfig';
import {useTranslation} from "react-i18next";
interface OwnProps extends Pick<RouteComponentProps<object>, 'history'> {
  scale: Scale;
}

interface StateProps {
  loadingEdit: boolean;
  loadingDel: boolean;
  editScaleSections: SectionOpt[];
}

interface DispatchProps {
  editScale(id: string, formData: EditScaleFormData): ApiReduxResp<Scale>;
  deleteScale(id: string, name: ContentLocalesI): ApiReduxResp<undefined>;
}

type Props = OwnProps & StateProps & DispatchProps;

const ScaleHeader: FC<Props> = (props: Props)=> {
  const [isEditFrmVisible, setEditFrmVisible] = useState<boolean>(false);
  const [deletionError, setDeletionError] = useState<string|null>(null);
  const { t } = useTranslation();

  const onEditSubmit = (formData: FormValuesI) =>
    props.editScale(props.scale.id, formData as unknown as EditScaleFormData).then((response) => {
      if (isSuccess(response)) {
        setEditFrmVisible(false);
      } else {
        logger.info('Error editing scale name', getLoggingContext(response,'ScaleHeader.jsx'));
      }
      return response;
    });

  const deleteScale = () =>
    props.deleteScale(props.scale.id, props.scale.name).then((resp) => {
      if (isSuccess(resp)) {
        goToList();
      }
      if (isError(resp)) {
        logger.info('Error delimiting the scale', getLoggingContext(resp, 'ScaleHeader.jsx'));
        setDeletionError("Error! Can't delete this scale.");
      }
      return resp;
    });

  const goToList = () => {
    props.history.push(routes.contentScales.scales);
  };

  const cancelEdit = () => {
    setEditFrmVisible(false);
  };

  return (
    <ItemDetailsHeader
      t={t}
      isEditFrmVisible={isEditFrmVisible}
      deleteItem={deleteScale}
      loadingDel={props.loadingDel}
      onBackClick={goToList}
      toggleEditFrm={setEditFrmVisible}
      backArrow={{
        key: 'x.scale.details.header.return_link_title',
        txt: t('x.scale.details.header.return_link_title'),
      }}
      supTitle={{
        key: 'x.scale.details.header.scale_type, x.scale.type.XXX',
        txt: `${t('x.scale.details.header.scale_type')}: ${t(`x.scale.type.${props.scale.visualization}`)}`,
      }}
      title={{ key: 'be', txt: translateContent(props.scale.name) }}
      description={{ key: 'be', txt: translateContent(props.scale.description) }}
      menuEdit={{
        key: 'x.scale.details.header.menu.edit_scale',
        txt: t('x.scale.details.header.menu.edit_scale'),
      }}
      menuDel={{
        key: 'x.scale.details.header.menu.delete_scale',
        txt: t('x.scale.details.header.menu.delete_scale'),
      }}
      deletionTitleKey="x.scale.delete_confirmation.headline"
      deletionContentKey="x.scale.delete_confirmation.content"
      dialogTitle={{ key: 'x.scale.err_delete_referenced', txt: t('x.scale.err_delete_referenced') }}
      dialogDesc={{
        key: 'x.scale.details.notification_modal.description',
        txt: t('x.scale.details.notification_modal.description'),
      }}
      deletionError={deletionError}
    >
      <TranslatableSectionForm
        t={t}
        titleKey="x.scale.details.header.edit.header"
        form={`editScale_${props.scale.id}`}
        sections={props.editScaleSections}
        onSubmit={onEditSubmit}
        onCancel={cancelEdit}
        loadingSave={props.loadingEdit}
        inContainer
      />
    </ItemDetailsHeader>
  );
}

const mapStateToProps = ({ scales }: StoreI, { scale }: OwnProps): StateProps => {
  const editScaleFields = [
    {
      value: scale.name,
    },
    {
      value: scale.description,
    },
  ].map((val, i) => ({
    ...nameDescrFields[i],
    ...val,
  }));

  return {
    loadingEdit: scales.loadingEdit,
    loadingDel: scales.loadingDel,
    editScaleSections: [
      {
        name: 'info',
        fields: editScaleFields,
      },
    ],
  };
};

const mapDispatchToProps = (dispatch: ThunkMWDispatch): DispatchProps => ({
  editScale: (id, formData) => dispatch(editScale(id, formData)),
  deleteScale: (id, name: ContentLocalesI) => dispatch(deleteScale(id, name)),
});

// @ts-expect-error ThunkMWDispatch is different from what React expects
const ScaleHeaderC = connect(mapStateToProps, mapDispatchToProps)(ScaleHeader);

export default ScaleHeaderC;
